import { useMemo } from 'react';
import { InfiniteScrollList } from '@/components/InfiniteScrollList';
import { ReturnRow } from './ReturnRow';
import type { LedgerReturn } from '@/api/types';

interface ReturnsListProps {
    /** Array of ledger returns to display. */
    returns: LedgerReturn[];
    /** The ledger ID these returns belong to. */
    ledgerId: number;
    /** Ledger's confirmed updated date timestamp (null = never confirmed). */
    confirmedUpdatedDate: number | null;
    /** ID of the return currently being edited, or null if none. */
    editingReturnId: number | null;
    /** Callback when a row's edit button is clicked. */
    onEdit: (returnId: number) => void;
    /** Callback when editing is cancelled. */
    onCancelEdit: () => void;
    /** Callback when save is complete. */
    onSaveComplete: () => void;
    /** Whether there are more pages to load. */
    hasNextPage: boolean;
    /** Whether the next page is currently being fetched. */
    isFetchingNextPage: boolean;
    /** Callback to load the next page. */
    onLoadMore: () => void;
}

interface DateGroup {
    dateKey: string;
    formattedDate: string;
    items: LedgerReturn[];
}

/**
 * Groups returns by date and returns an array of groups.
 * Each group has a dateKey, formatted date string, and array of items.
 */
function groupReturnsByDate(returns: LedgerReturn[]): DateGroup[] {
    const groups: Map<string, DateGroup> = new Map();

    for (const returnItem of returns) {
        // Use dateFormatted (Y-m-d) as the date key for grouping
        const dateKey = returnItem.dateFormatted;
        // Parse the ISO 8601 date string for display formatting
        const date = new Date(returnItem.date);
        const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });

        const existing = groups.get(dateKey);
        if (existing) {
            existing.items.push(returnItem);
        } else {
            groups.set(dateKey, {
                dateKey,
                formattedDate,
                items: [returnItem],
            });
        }
    }

    return Array.from(groups.values());
}

/**
 * List of ledger returns with infinite scroll support.
 * Uses InfiniteScrollList wrapper for pagination.
 * Groups returns by date, showing date headers for groups with multiple entries.
 */
export function ReturnsList({
    returns,
    ledgerId,
    confirmedUpdatedDate,
    editingReturnId,
    onEdit,
    onCancelEdit,
    onSaveComplete,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
}: ReturnsListProps) {
    const dateGroups = useMemo(() => groupReturnsByDate(returns), [returns]);

    return (
        <InfiniteScrollList
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={onLoadMore}
            loadingText="Loading more..."
            className="space-y-2"
        >
            {dateGroups.map((group) => (
                <div
                    key={group.dateKey}
                    className={
                        group.items.length > 1
                            ? 'border-l-2 border-accent dark:border-primary-light pl-2'
                            : ''
                    }
                >
                    <div className="space-y-2">
                        {group.items.map((returnItem) => (
                            <ReturnRow
                                key={returnItem.id}
                                returnItem={returnItem}
                                ledgerId={ledgerId}
                                confirmedUpdatedDate={confirmedUpdatedDate}
                                isEditing={editingReturnId === returnItem.id}
                                onEdit={() => onEdit(returnItem.id)}
                                onCancelEdit={onCancelEdit}
                                onSaveComplete={onSaveComplete}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </InfiniteScrollList>
    );
}
